import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Search, Heart, Briefcase, MessageCircle, User } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useUser } from "@/hooks/useUser";
import { accountNavigationSession } from "@/lib/account-navigation-session";
import { fetchUnreadMessageCount, isMissingProfileError } from "@/lib/users-api";

interface TabDef {
  href: string;
  label: string;
  icon: typeof Search;
  /** prefixes that mark this tab active */
  match: (loc: string) => boolean;
  badge?: number;
}

/**
 * Mobile-only renter bottom tab bar (Airbnb-style primary nav).
 *
 * Renders ONLY on phones (`md:hidden`) and ONLY for signed-in users — logged
 * out visitors keep the hamburger. The desktop header is untouched. It is
 * mounted once in the public/renter shell in App.tsx; the shell decides which
 * routes suppress it (car detail + checkout, which own their own fixed bottom
 * bars, and the full-screen chat thread).
 */
export function BottomTabBar() {
  const { t } = useTranslation();
  const { session } = useUser();
  const [location] = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);

  const token = accountNavigationSession(session)?.access_token ?? null;

  useEffect(() => {
    if (!token) {
      setUnreadMessages(0);
      return;
    }
    let cancelled = false;
    fetchUnreadMessageCount(token)
      .then((res) => {
        if (!cancelled) setUnreadMessages(res.count ?? 0);
      })
      .catch((err) => {
        // Task #5423 — an authenticated-but-no-profile user (valid session,
        // no public.users row) 404s here with "User profile not found". That
        // is a known state, not an error: there are no messages until the
        // profile exists, so degrade to a 0 badge and stay silent. The badge
        // is best-effort for genuine failures too; never block nav on a
        // failed count.
        if (isMissingProfileError(err)) {
          if (!cancelled) setUnreadMessages(0);
          return;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, location]);

  if (!token) return null;

  const tabs: TabDef[] = [
    {
      href: "/search",
      label: t("nav.search", { defaultValue: "Search" }),
      icon: Search,
      match: (loc) => loc === "/" || loc.startsWith("/search"),
    },
    {
      href: "/favourites",
      label: t("nav.favourites", { defaultValue: "Favourites" }),
      icon: Heart,
      match: (loc) => loc.startsWith("/favourites"),
    },
    {
      href: "/trips",
      label: t("nav.trips", { defaultValue: "Trips" }),
      icon: Briefcase,
      match: (loc) => loc.startsWith("/trips"),
    },
    {
      href: "/messages",
      label: t("nav.messages", { defaultValue: "Messages" }),
      icon: MessageCircle,
      match: (loc) => loc.startsWith("/messages"),
      badge: unreadMessages,
    },
    {
      href: "/profile",
      label: t("nav.profile", { defaultValue: "Profile" }),
      icon: User,
      match: (loc) => loc.startsWith("/profile"),
    },
  ];

  return (
    <>
      {/* In-flow spacer so page content / footer can scroll clear of the
          fixed bar. Only present when the bar renders (signed-in mobile). */}
      <div aria-hidden className="h-[var(--tabbar-h)] md:hidden" />
      <nav
        aria-label={t("nav.primary_aria", { defaultValue: "Primary" })}
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border material-panel pb-safe md:hidden"
      >
      <ul className="mx-auto flex max-w-[640px] items-stretch justify-around">
        {tabs.map((tab) => {
          const active = tab.match(location);
          const Icon = tab.icon;
          return (
            <li key={tab.href} className="flex-1">
              <Link
                href={tab.href}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "relative flex min-h-[48px] flex-col items-center justify-center gap-0.5 px-1 py-1.5 text-[11px] font-medium transition-colors",
                  active ? "text-brand" : "text-muted-foreground hover:text-foreground",
                )}
              >
                <span className="relative">
                  <Icon
                    className={cn("h-5 w-5", active && "fill-brand/10")}
                    strokeWidth={active ? 2.4 : 2}
                  />
                  {tab.badge != null && tab.badge > 0 && (
                    <>
                      <span className="sr-only">, {tab.badge} {t("nav.unread_badge_suffix", { defaultValue: "unread" })}</span>
                      <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white" aria-hidden="true">
                        {tab.badge > 9 ? "9+" : tab.badge}
                      </span>
                    </>
                  )}
                </span>
                <span className="leading-none">{tab.label}</span>
              </Link>
            </li>
          );
        })}
        </ul>
      </nav>
    </>
  );
}
